/* eslint-disable  */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button, Space, Tooltip, Select, DatePicker, Popconfirm, Tag, Badge } from 'antd';
import { useRecoilValue } from 'recoil';
import { checkPermissions } from '../../../utils/permissions';
import TimesAgo from '../../../components/timeAgo/timeAgo';
import { appConfigAtomState } from '../../../state/reducerAtoms/app.atom';
import { connectSocket, joinUniversityRoom } from '../../../services/socket';
import ApiCaller from '../../../services/api/general';

import AppTable from '../../../components/table/index';
import './index.scss';

import { getCurrentFacilityURL } from '../../../helpers/helper.functions';
import { translateThis } from '../../../helpers/language.helper';
import PageLayout from '../../app';

import { DATE_FORMAT, DATE_FORMAT_YEAR_TIME_SHORT } from '../../../constants';
import SVGIcons from '../../../assets/images/icons/svgIcons';
import AppPopOverMenuv2 from '../../../components/appPopOverMenuv2';


const DEFAULT_COLUMN_ORDER = [
    'ticketNumber', 'userName', 'userId', 'phoneNumber', 'createdAt', 'summaryTitle',
    'topicStatus', 'aging', 'agentName', 'status', 'interestIn',
    'goal', 'timelineUrgency', 'qualificationStatus', 'actions'
];

const ServiceTicketList = () => {
    const appState = useRecoilValue(appConfigAtomState);
    const profile = appState?.profile || {};

    const facilityURL = getCurrentFacilityURL();
    const navigator = useNavigate();
    const [refreshReason, setRefreshReason] = React.useState('')
    const [selectedAgentType, setSelectedAgentType] = React.useState('');

    // Live human chat requests
    const [liveRequests, setLiveRequests] = useState([]);

    // Hoisted so socket handlers can call it directly — always returns authoritative
    // populated data (moduleId as object) from the API, fixing the real-time blink issue.
    const fetchLiveRequests = React.useCallback(async () => {
        try {
            const apiCaller = new ApiCaller('inbox/human-chat/list');
            const result = await apiCaller.getCustomList('', { query: 'statuses=pending,active' });
            if (Array.isArray(result)) {
                setLiveRequests(result);
            }
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        fetchLiveRequests();

        const socket = connectSocket();
        const universityId = profile?.universityId;
        if (universityId) joinUniversityRoom(universityId);

        const onNewRequest = () => {
            // Re-fetch so moduleId is a populated object — manual state patch from
            // raw socket data had moduleId as a string, breaking pendingModuleIds lookup.
            fetchLiveRequests();
            setRefreshReason(`new-req-${Date.now()}`);
        };

        const onTicketUpdated = () => {
            // Re-fetch on any status change so completed/timed_out rows stop blinking
            // immediately without a manual page refresh.
            fetchLiveRequests();
        };

        const onNewTicket = () => {
            setRefreshReason(`new-ticket-${Date.now()}`);
        };

        socket.on('new_agent_request', onNewRequest);
        socket.on('ticket_updated', onTicketUpdated);
        socket.on('new_ticket', onNewTicket);
        return () => {
            socket.off('new_agent_request', onNewRequest);
            socket.off('ticket_updated', onTicketUpdated);
            socket.off('new_ticket', onNewTicket);
        };
    }, [profile?.universityId, fetchLiveRequests]);

    // Filter states
    const [filters, setFilters] = React.useState({
        dateRange: null,
        state: null,
        status: null,
        agent: null,
        urgency: null,
        qualificationStatus: null
    });
    // Column visibility state
    const [columnVisibility, setColumnVisibility] = React.useState(() => {
        const defaults = {
            ticketNumber: true,
            userName: true,
            userId: true,
            phoneNumber: true,
            createdAt: true,
            summaryTitle: true,
            topicStatus: true,
            aging: true,
            agentName: true,
            status: true,
            interestIn: true,
            goal: true,
            timelineUrgency: true,
            qualificationStatus: true,
            actions: true,
        };
        const saved = JSON.parse(localStorage.getItem('serviceTicketTableColumns')) || {};
        return { ...defaults, ...saved };
    });

    // Column order state
    const [columnOrder, setColumnOrder] = React.useState(() => {
        const saved = JSON.parse(localStorage.getItem('serviceTicketColumnOrder'));
        if (saved && Array.isArray(saved)) {
            const valid = saved.filter(k => DEFAULT_COLUMN_ORDER.includes(k));
            const missing = DEFAULT_COLUMN_ORDER.filter(k => !valid.includes(k));
            return [...valid, ...missing];
        }
        return DEFAULT_COLUMN_ORDER;
    });

    const moveColumn = (key, direction) => {
        setColumnOrder(prev => {
            const idx = prev.indexOf(key);
            if (idx === -1) return prev;
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (swapIdx < 0 || swapIdx >= prev.length) return prev;
            const next = [...prev];
            const temp = next[idx];
            next[idx] = next[swapIdx];
            next[swapIdx] = temp;
            localStorage.setItem('serviceTicketColumnOrder', JSON.stringify(next));
            return next;
        });
    };

    const draggedKey = useRef(null);
    const [dragOverKey, setDragOverKey] = React.useState(null);

    const moveColumnToIndex = (fromKey, toKey) => {
        if (fromKey === toKey) return;
        setColumnOrder(prev => {
            const next = prev.filter(k => k !== fromKey);
            const toIdx = next.indexOf(toKey);
            next.splice(toIdx, 0, fromKey);
            localStorage.setItem('serviceTicketColumnOrder', JSON.stringify(next));
            return next;
        });
    };

    // Dynamic filter options
    const [agentOptions, setAgentOptions] = React.useState([]);
    const stateOptions = [
        { value: 'Resolved', label: translateThis('Resolved') },
        { value: 'Not resolved', label: translateThis('Not Resolved') },
        { value: 'Next Step', label: translateThis('Next Step') },
        { value: 'Human Req.', label: translateThis('Human Intervention Needed') }
    ];

    const statusOptions = [
        { value: 'COMPLETE', label: translateThis('Complete') },
        { value: 'INPROGRESS', label: translateThis('In Progress') }
    ];

    const urgencyOptions = [
        { value: 'Just Researching', label: translateThis('Just Researching') },
        { value: '3+ Months', label: translateThis('3+ Months') },
        { value: '1–3 Months', label: translateThis('1–3 Months') },
        { value: '2–4 Weeks', label: translateThis('2–4 Weeks') },
        { value: 'Within 1 Week', label: translateThis('Within 1 Week') },
        { value: 'Ready Now', label: translateThis('Ready Now') }
    ];

    const qualificationStatusOptions = [
        { value: 'Hot', label: translateThis('Hot') },
        { value: 'Warm', label: translateThis('Warm') },
        { value: 'Cold', label: translateThis('Cold') }
    ];
    // Fetch filter options from API
    React.useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                // Fetch agents
                const agentsApiCaller = new ApiCaller('modules?limit=200&page=1&searchTerm=&sortBy=createdAt:desc');
                const agentsResponse = await agentsApiCaller.getData();
                if (agentsResponse?.data?.results) {
                    setAgentOptions(agentsResponse.data.results?.map(agent => ({
                        value: agent?.id,
                        label: agent?.agentName || agent?.title,
                        agentType: agent?.agentType || 'customer_support'
                    })));
                }
            } catch (error) {
                console.error('Error fetching filter options:', error);
                setAgentOptions([]);
            }
        };

        fetchFilterOptions();
    }, []);

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
        // Detect agent type when agent filter changes
        if (filterType === 'agent') {
            if (value) {
                const selectedAgent = agentOptions.find(opt => opt.value === value);
                setSelectedAgentType(selectedAgent?.agentType || 'customer_support');
            } else {
                setSelectedAgentType('');
            }
        }
    };

    const clearFilters = () => {
        setFilters({
            dateRange: null,
            state: null,
            status: null,
            agent: null,
            urgency: null,
            qualificationStatus: null
        });
        setSelectedAgentType('');
    };

    const hasActiveFilters = () => {
        return filters.dateRange || filters.state || filters.status || filters.agent || filters.urgency || filters.qualificationStatus;
    };

    const isWithinFourMinutes = (dateTime) => {
        return (new Date() - new Date(dateTime)) <= (4 * 60 * 1000);
    };

    // Qualification status color helper
    const getQualificationColor = (status) => {
        switch (status) {
            case 'Hot': return 'red';
            case 'Warm': return 'orange';
            case 'Cold': return 'blue';
            default: return 'default';
        }
    };

    // Stage color helper
    const getStageColor = (stage) => {
        switch (stage) {
            case 'Scheduled': return 'cyan';
            case 'Closed Won': return 'green';
            case 'Closed Lost': return 'red';
            case 'New': return 'default';
            default: return 'default';
        }
    };

    // Support column definitions (existing)
    const columnDefinitions = {
        ticketNumber: {
            key: 'ticketNumber',
            dataIndex: 'ticketNumber',
            title: `${translateThis('#')}`,
            width: 60,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } });
                }}>{text}</span>
            )
        },
        userName: {
            title: `${translateThis('Name')}`,
            dataIndex: 'userName',
            key: 'userName',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } });
                }}>{text}</span>
            )
        },
        userId: {
            title: `${translateThis('Email')}`,
            dataIndex: 'userId',
            key: 'userId',
            width: 200,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } });
                }}>{text}</span>
            )
        },
        phoneNumber: {
            title: `${translateThis('Phone')}`,
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } });
                }}>{text || '-'}</span>
            )
        },
        createdAt: {
            title: `${translateThis('Created')}`,
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => {
                    navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } });
                }}>{record.createdAt ? dayjs(record?.createdAt).format(DATE_FORMAT_YEAR_TIME_SHORT) : '-'}</span>
            )
        },
        summaryTitle: {
            title: `${translateThis('Topic')}`,
            dataIndex: 'summaryTitle',
            key: 'moduleId',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text} >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text}
                    </span>
                </Tooltip>
            )
        },
        topicStatus: {
            title: `${translateThis('State')}`,
            dataIndex: 'topicStatus',
            key: 'topicStatus',
            width: 130,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text} >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>{text}</span>
                </Tooltip>
            )
        },
        aging: {
            title: `${translateThis('Aging')}`,
            dataIndex: 'aging',
            key: 'aging',
            width: 110,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {record?.questions?.[record.questions?.length - 1]?.createdAt && isWithinFourMinutes(record?.questions?.[record.questions?.length - 1]?.createdAt) && record?.status === 'INPROGRESS' && <span className="aging-pulse"><span className="aging-pulse-ring" /><SVGIcons.RedColor /></span>}{' '}
                    <TimesAgo date={record?.questions?.[record.questions?.length - 1]?.createdAt} />
                </span>
            )
        },
        agentName: {
            title: `${translateThis('Agent Name')}`,
            dataIndex: 'moduleId',
            key: 'moduleId',
            width: 120,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text?.agentName} >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text?.agentName || text?.title || "--"}
                    </span>
                </Tooltip>
            )
        },
        status: {
            title: `${translateThis('Status')}`,
            dataIndex: 'status',
            key: 'status',
            width: 100,
            ellipsis: true,
            render: (text, record) => {
                if (checkPermissions(profile, "Mark as completed")) {
                    return (
                        <Tooltip title={`Mark as ${text === 'COMPLETE' ? 'INPROGRESS' : 'COMPLETE'}`} >
                            <Button
                                className='status-btn'
                                shape="circle"
                                onClick={() => markAsCompleted(record.id, text)}>
                                {text === 'COMPLETE' ? <SVGIcons.StatusActiveIcon /> : <SVGIcons.StatusInactiveIcon />}
                            </Button>
                        </Tooltip >
                    )
                }
                return <Tooltip title={text} >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text}
                    </span>
                </Tooltip>
            },
        },
        interestIn: {
            title: `${translateThis('Interest In')}`,
            dataIndex: ['salesData', 'interestIn'],
            key: 'interestIn',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text}>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text || '-'}
                    </span>
                </Tooltip>
            )
        },
        goal: {
            title: `${translateThis('Goal')}`,
            dataIndex: ['salesData', 'goal'],
            key: 'goal',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text}>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text || '-'}
                    </span>
                </Tooltip>
            )
        },
        timelineUrgency: {
            title: `${translateThis('Timeline / Urgency')}`,
            dataIndex: ['salesData', 'timelineUrgency'],
            key: 'timelineUrgency',
            width: 170,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {text ? <Tag>{text}</Tag> : '-'}
                </span>
            )
        },
        qualificationStatus: {
            title: `${translateThis('Qualification Status')}`,
            dataIndex: ['salesData', 'qualificationStatus'],
            key: 'qualificationStatus',
            width: 180,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {text ? <Tag color={getQualificationColor(text)}>{text}</Tag> : '-'}
                </span>
            )
        },
        actions: {
            title: `${translateThis('Actions')}`,
            dataIndex: 'actions',
            key: 'actions',
            width: 160,
            render: (_, record) => {
                const liveReq = liveRequests.find(r => r.contextId === record.contextId);
                const isPending = liveReq?.status === 'pending';
                const humanSupportColor = isPending ? '#faad14' : '#52c41a';
                const humanSupportTitle = isPending
                    ? translateThis('Human support pending')
                    : translateThis('Human support active');
                return (
                    <Space size={6} className='action-table-btns'>
                        <Tooltip title={translateThis('View')}>
                            <Button
                                shape="circle"
                                onClick={() => navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } })}
                            >
                                <SVGIcons.EyeOpen />
                            </Button>
                        </Tooltip>
                        {liveReq && (
                            <Tooltip title={humanSupportTitle}>
                                <Button
                                    shape="circle"
                                    style={{ borderColor: humanSupportColor, color: humanSupportColor }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigator(`/${facilityURL}/ai-development/view-ticket/${liveReq._id || liveReq.ticketId}?type=human`);
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                                        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                                    </svg>
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title={translateThis('Delete')}>
                            <Popconfirm
                                title={translateThis('Confirm Delete')}
                                description={translateThis('Are you sure you want to delete this ticket?')}
                                okText={translateThis('Yes')}
                                cancelText={translateThis('Cancel')}
                                onConfirm={() => deleteTicket(record.id)}
                            >
                                <Button shape="circle"><SVGIcons.DeleteBlueIcon /></Button>
                            </Popconfirm>
                        </Tooltip>
                    </Space>
                );
            },
        },
    };

    // Sales-specific column definitions
    const salesColumnDefinitions = {
        ticketNumber: columnDefinitions.ticketNumber,
        userName: {
            ...columnDefinitions.userName,
            title: `${translateThis('Lead Name')}`,
        },
        createdAt: columnDefinitions.createdAt,
        interestIn: {
            title: `${translateThis('Interest In')}`,
            dataIndex: ['salesData', 'interestIn'],
            key: 'interestIn',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text}>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text || '-'}
                    </span>
                </Tooltip>
            )
        },
        goal: {
            title: `${translateThis('Goal')}`,
            dataIndex: ['salesData', 'goal'],
            key: 'goal',
            width: 150,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={text}>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                        {text || '-'}
                    </span>
                </Tooltip>
            )
        },
        timelineUrgency: {
            title: `${translateThis('Timeline / Urgency')}`,
            dataIndex: ['salesData', 'timelineUrgency'],
            key: 'timelineUrgency',
            width: 140,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {text ? <Tag>{text}</Tag> : '-'}
                </span>
            )
        },
        qualificationStatus: {
            title: `${translateThis('Qualification')}`,
            dataIndex: ['salesData', 'qualificationStatus'],
            key: 'qualificationStatus',
            width: 120,
            ellipsis: true,
            render: (text, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {text ? <Tag color={getQualificationColor(text)}>{text}</Tag> : '-'}
                </span>
            )
        },
        stage: {
            title: `${translateThis('Stage')}`,
            dataIndex: 'salesData',
            key: 'stage',
            width: 120,
            ellipsis: true,
            render: (salesData, record) => (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span style={{ cursor: 'pointer' }} onClick={() => { navigator(`/${facilityURL}/ai-development/view-ticket/${record.id}`, { state: { data: record } }); }}>
                    {salesData?.stage ?
                        <Tag color={getStageColor(salesData.stage)}>{salesData.stage}</Tag>
                        : <Tag>New</Tag>}
                </span>
            )
        },
        aging: columnDefinitions.aging,
        actions: columnDefinitions.actions,
    };

    // Choose active column definitions based on selected agent type
    const activeColumnDefinitions = selectedAgentType === 'sales' ? salesColumnDefinitions : columnDefinitions;

    // Generate table columns based on visibility and order
    const tableColumns = columnOrder
        .filter(key => columnVisibility[key] !== false && activeColumnDefinitions[key])
        .map(key => activeColumnDefinitions[key]);

    const deleteTicket = (id) => {
        const apiCaller = new ApiCaller('modules/conversations');
        apiCaller.customPatchData(id, { isDeleted: true })
            .then(() => setRefreshReason(`deleted-${id}-${Date.now()}`))
            .catch(() => null);
    };

    const markAsCompleted = (id, status) => {
        const updatedStatus = status === 'INPROGRESS' ? 'COMPLETE' : 'INPROGRESS';
        const apiCaller = new ApiCaller(`ai/${id}/update-status`);
        apiCaller.postData({ status: updatedStatus }).then((_) => setRefreshReason(`${id}-${updatedStatus}`))
    }
    const getDataURL = () => `modules/conversations`;

    const pendingContextIds = React.useMemo(
        () => new Set(liveRequests.filter(r => r.status === 'pending').map(r => r.contextId).filter(Boolean)),
        [liveRequests]
    );
    const activeContextIds = React.useMemo(
        () => new Set(liveRequests.filter(r => r.status === 'active').map(r => r.contextId).filter(Boolean)),
        [liveRequests]
    );

    const [showFilters, setShowFilters] = useState(false);

    return (
        <>
            {checkPermissions(profile, "See Tickets") ?
                <>
                    <style>
                        {`
                    .date-picker-white-placeholder .ant-picker-input > input::placeholder {
                        color: #fff !important;
                    }
                    .date-picker-white-placeholder .ant-picker-input > input::-webkit-input-placeholder {
                        color: #fff !important;
                    }
                    .date-picker-white-placeholder .ant-picker-input > input::-moz-placeholder {
                        color: #fff !important;
                    }
                    .date-picker-white-placeholder .ant-picker-input > input:-ms-input-placeholder {
                        color: #fff !important;
                    }
                    #column-visibility-menu > div > ul {
                        background: linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%) !important;
                        color: #fff !important;
                    }
                    .col-vis-pop .ant-menu-item:active,
                    .col-vis-pop .ant-menu-item:not(.ant-menu-item-selected):active {
                        background-color: var(--navBg) !important;
                    }
                    .col-vis-pop .ant-menu-item:hover {
                        background-color: var(--navBg) !important;
                    }
                    .aging-pulse {
                        position: relative;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 28px;
                        height: 28px;
                        flex-shrink: 0;
                    }
                    .aging-pulse-ring {
                        position: absolute;
                        width: 25px;
                        height: 25px;
                        border-radius: 50%;
                        background: rgba(188, 64, 28, 0.95);
                        animation: aging-pulse-expand 1.8s ease-out infinite;
                    }
                    @keyframes aging-pulse-expand {
                        0%   { transform: scale(0.5); opacity: 0.8; }
                        100% { transform: scale(1.8); opacity: 0; }
                    }
                `}
                    </style>
                    <PageLayout
                        pageName={'Tickets'}
                        pageTitle={'Modules'}
                        bodyClassName={`module-main-wrp`}
                        key={`${appState?.selectedLang}`}
                    >
                        <div className="container">
                            <div className="main-content-wrapper AI-module-list f-wrp">
                                <div className="module-list-box appTable-list f-wrp">
                                    {showFilters && (
                                        <div className='filter-header'>
                                            <div className='filter-header-left'>
                                                <div className='filter-header-left-item'>
                                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">

                                                        <div className="flex flex-wrap items-end gap-4"
                                                            style={{
                                                                gap: '1rem', marginTop: '25px',
                                                                background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                borderRadius: '50px',
                                                                padding: '20px',
                                                                color: 'white',
                                                                fontFamily: 'var(--Inter)',
                                                                fontWeight: '700',
                                                                cursor: 'pointer',
                                                                minWidth: '100px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                marginBottom: '20px'
                                                            }}
                                                        >
                                                            {/* From Date Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="from-date-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginBottom: '12px' }}>
                                                                    {translateThis('From')}
                                                                </label>
                                                                <DatePicker
                                                                    style={{
                                                                        color: '#f0f0f0',
                                                                        marginLeft: '12px',
                                                                        backgroundColor: filters.dateRange && filters.dateRange[0] ? 'rgba(117, 23, 248, 0.1)' : 'transparent',
                                                                        border: filters.dateRange && filters.dateRange[0] ? '1px solid #7517F8' : '1px solid #d9d9d9',
                                                                        borderRadius: '6px'
                                                                    }}
                                                                    id="from-date-filter"
                                                                    className="w-full date-picker-white-placeholder"
                                                                    placeholder={translateThis('Start Date')}
                                                                    value={filters.dateRange ? filters.dateRange[0] : null}
                                                                    onChange={(date) => {
                                                                        const endDate = filters.dateRange ? filters.dateRange[1] : null;
                                                                        handleFilterChange('dateRange', date ? [date, endDate] : null);
                                                                    }}
                                                                    format={`${DATE_FORMAT} h:mm A`}
                                                                    showTime={{ format: 'h:mm A' }}
                                                                    inputReadOnly
                                                                    allowClear
                                                                />
                                                            </div>

                                                            {/* To Date Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="to-date-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginBottom: '12px' }}>
                                                                    {translateThis('To')}
                                                                </label>
                                                                <DatePicker
                                                                    style={{
                                                                        color: '#f0f0f0',
                                                                        marginLeft: '12px',
                                                                        backgroundColor: filters.dateRange && filters.dateRange[1] ? 'rgba(117, 23, 248, 0.1)' : 'transparent',
                                                                        border: filters.dateRange && filters.dateRange[1] ? '1px solid #7517F8' : '1px solid #d9d9d9',
                                                                        borderRadius: '6px'
                                                                    }}
                                                                    id="to-date-filter"
                                                                    className="w-full date-picker-white-placeholder"
                                                                    placeholder={translateThis('End Date')}
                                                                    value={filters.dateRange ? filters.dateRange[1] : null}
                                                                    onChange={(date) => {
                                                                        const startDate = filters.dateRange ? filters.dateRange[0] : null;
                                                                        handleFilterChange('dateRange', date ? [startDate, date] : null);
                                                                    }}
                                                                    format={`${DATE_FORMAT} h:mm A`}
                                                                    showTime={{ format: 'h:mm A' }}
                                                                    inputReadOnly
                                                                    allowClear
                                                                />
                                                            </div>

                                                            {/* State Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="state-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginLeft: '12px' }}>
                                                                    {translateThis('State')}
                                                                </label>
                                                                <Select
                                                                    style={{ color: '#f0f0f0', marginLeft: '12px' }}
                                                                    id="state-filter"
                                                                    className="w-full"
                                                                    placeholder={translateThis('Select State')}
                                                                    value={filters.state}
                                                                    onChange={(value) => handleFilterChange('state', value)}
                                                                    allowClear
                                                                    options={stateOptions}
                                                                />
                                                            </div>

                                                            {/* Status Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="status-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginLeft: '12px' }}>
                                                                    {translateThis('Status')}
                                                                </label>
                                                                <Select
                                                                    style={{ color: '#f0f0f0', marginLeft: '12px' }}
                                                                    id="status-filter"
                                                                    className="w-full"
                                                                    placeholder={translateThis('Select Status')}
                                                                    value={filters.status}
                                                                    onChange={(value) => handleFilterChange('status', value)}
                                                                    allowClear
                                                                    options={statusOptions}
                                                                />
                                                            </div>

                                                            {/* Agent Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="agent-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginLeft: '12px' }}>
                                                                    {translateThis('Agent Name')}
                                                                </label>
                                                                <Select
                                                                    style={{ color: '#f0f0f0', marginLeft: '12px' }}
                                                                    id="agent-filter"
                                                                    className="w-full"
                                                                    placeholder={translateThis('Select Agent')}
                                                                    value={filters.agent}
                                                                    onChange={(value) => handleFilterChange('agent', value)}
                                                                    allowClear
                                                                    options={agentOptions}
                                                                />
                                                            </div>

                                                            {/* Urgency Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="urgency-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginLeft: '12px' }}>
                                                                    {translateThis('Urgency')}
                                                                </label>
                                                                <Select
                                                                    style={{ color: '#f0f0f0', marginLeft: '12px' }}
                                                                    id="urgency-filter"
                                                                    className="w-full"
                                                                    placeholder={translateThis('Select Urgency')}
                                                                    value={filters.urgency}
                                                                    onChange={(value) => handleFilterChange('urgency', value)}
                                                                    allowClear
                                                                    options={urgencyOptions}
                                                                />
                                                            </div>

                                                            {/* Qualification Status Filter */}
                                                            <div className="flex flex-col space-y-4 min-w-[150px]">
                                                                <label htmlFor="qualification-filter" className="block text-sm font-medium" style={{ color: '#f0f0f0', marginLeft: '12px' }}>
                                                                    {translateThis('Qualification Status')}
                                                                </label>
                                                                <Select
                                                                    style={{ color: '#f0f0f0', marginLeft: '12px' }}
                                                                    id="qualification-filter"
                                                                    className="w-full"
                                                                    placeholder={translateThis('Select Qualification')}
                                                                    value={filters.qualificationStatus}
                                                                    onChange={(value) => handleFilterChange('qualificationStatus', value)}
                                                                    allowClear
                                                                    options={qualificationStatusOptions}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Active Filters Display */}
                                                        {hasActiveFilters() && (
                                                            <div className="mt-4 pt-4 border-t border-gray-200" style={{ color: '#f0f0f0', marginBottom: '12px' }} >
                                                                <div className="flex flex-wrap gap-2">
                                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800" style={{ float: 'right', marginBottom: '2px' }}>
                                                                        <button
                                                                            onClick={clearFilters}
                                                                            className="text-sm font-medium"
                                                                            style={{
                                                                                background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                border: 'none',
                                                                                borderRadius: '50px',
                                                                                padding: '5px 15px',
                                                                                color: 'white',
                                                                                fontFamily: 'var(--Inter)',
                                                                                fontWeight: '700',
                                                                                fontSize: '13px',
                                                                                cursor: 'pointer',
                                                                                minWidth: '100px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center'
                                                                            }}
                                                                        >
                                                                            {translateThis('Clear All')}
                                                                        </button>
                                                                    </span>
                                                                    {filters.dateRange && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                            {translateThis('Date')}: {dayjs(filters.dateRange[0]).format(DATE_FORMAT_YEAR_TIME_SHORT)} - {filters.dateRange[1] ? dayjs(filters.dateRange[1]).format(DATE_FORMAT_YEAR_TIME_SHORT) : "Today"}
                                                                            <button
                                                                                onClick={() => handleFilterChange('dateRange', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px',
                                                                                    marginRight: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {filters.state && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            {translateThis('State')}: {stateOptions.find(opt => opt.value === filters.state)?.label}
                                                                            <button
                                                                                onClick={() => handleFilterChange('state', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px',
                                                                                    marginRight: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {filters.status && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                            {translateThis('Status')}: {statusOptions.find(opt => opt.value === filters.status)?.label}
                                                                            <button
                                                                                onClick={() => handleFilterChange('status', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px',
                                                                                    marginRight: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {filters.agent && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                                            {translateThis('Agent')}: {agentOptions.find(opt => opt.value === filters.agent)?.label}
                                                                            <button
                                                                                onClick={() => handleFilterChange('agent', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {filters.urgency && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                            {translateThis('Urgency')}: {urgencyOptions.find(opt => opt.value === filters.urgency)?.label}
                                                                            <button
                                                                                onClick={() => handleFilterChange('urgency', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px',
                                                                                    marginRight: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                    {filters.qualificationStatus && (
                                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                            {translateThis('Qualification')}: {qualificationStatusOptions.find(opt => opt.value === filters.qualificationStatus)?.label}
                                                                            <button
                                                                                onClick={() => handleFilterChange('qualificationStatus', null)}
                                                                                style={{
                                                                                    background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '20px',
                                                                                    height: '20px',
                                                                                    color: 'white',
                                                                                    fontFamily: 'var(--Inter)',
                                                                                    fontWeight: '700',
                                                                                    fontSize: '12px',
                                                                                    cursor: 'pointer',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    marginLeft: '8px',
                                                                                    marginRight: '8px'
                                                                                }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    )}

                                                                </div>
                                                            </div>
                                                        )}


                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className='btn-header'>
                                        <button
                                            onClick={() => setShowFilters(!showFilters)}
                                            className="text-sm font-medium"
                                            style={{
                                                background: 'linear-gradient(136.67deg, #7517F8 8.34%, #C438EF 95.26%)',
                                                border: 'none',
                                                borderRadius: '50px',
                                                padding: '5px 15px',
                                                color: 'white',
                                                fontFamily: 'var(--Inter)',
                                                fontWeight: '700',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                minWidth: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px'
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
                                            </svg>
                                            {translateThis('Filter')}
                                        </button>
                                    </div>
                                    <div className='btn-header'>
                                        <AppPopOverMenuv2 id="column-visibility-menu" overlayClassName="col-vis-pop"
                                            options={columnOrder.filter(key => columnDefinitions[key]).map((key, idx, arr) => ({
                                                label: (
                                                    <div
                                                        draggable
                                                        onDragStart={(e) => { draggedKey.current = key; e.dataTransfer.effectAllowed = 'move'; }}
                                                        onDragOver={(e) => { e.preventDefault(); setDragOverKey(key); }}
                                                        onDragLeave={() => setDragOverKey(null)}
                                                        onDrop={(e) => { e.preventDefault(); moveColumnToIndex(draggedKey.current, key); setDragOverKey(null); draggedKey.current = null; }}
                                                        onDragEnd={() => { setDragOverKey(null); draggedKey.current = null; }}
                                                        style={{
                                                            color: '#fff', display: 'flex', alignItems: 'center',
                                                            justifyContent: 'space-between', width: '100%', gap: '8px',
                                                            opacity: dragOverKey === key ? 0.6 : 1,
                                                            borderTop: dragOverKey === key ? '2px solid rgba(255,255,255,0.8)' : '2px solid transparent',
                                                            transition: 'border-color 0.1s',
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ cursor: 'grab', color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1, userSelect: 'none' }}>⠿</span>
                                                            {columnVisibility[key] !== false ? (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                                    <circle cx="12" cy="12" r="3" />
                                                                </svg>
                                                            ) : (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                                                    <line x1="1" y1="1" x2="23" y2="23" />
                                                                </svg>
                                                            )}
                                                            <span style={{ color: '#fff', fontSize: '13px' }}>
                                                                {columnDefinitions[key].title}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); moveColumn(key, 'up'); }}
                                                                disabled={idx === 0}
                                                                style={{ background: 'none', border: 'none', color: idx === 0 ? 'rgba(255,255,255,0.3)' : '#fff', cursor: idx === 0 ? 'default' : 'pointer', padding: '0 3px', fontSize: '11px', lineHeight: 1 }}
                                                            >▲</button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); moveColumn(key, 'down'); }}
                                                                disabled={idx === arr.length - 1}
                                                                style={{ background: 'none', border: 'none', color: idx === arr.length - 1 ? 'rgba(255,255,255,0.3)' : '#fff', cursor: idx === arr.length - 1 ? 'default' : 'pointer', padding: '0 3px', fontSize: '11px', lineHeight: 1 }}
                                                            >▼</button>
                                                        </div>
                                                    </div>
                                                ),
                                                onClick: () => {
                                                    setColumnVisibility(prev => ({
                                                        ...prev,
                                                        [key]: prev[key] === false ? true : false
                                                    }));
                                                    localStorage.setItem('serviceTicketTableColumns', JSON.stringify({
                                                        ...columnVisibility,
                                                        [key]: columnVisibility[key] === false ? true : false
                                                    }));
                                                }
                                            }))}
                                        />
                                    </div>
                                    <AppTable
                                        queryParams={filters}
                                        columns={tableColumns}
                                        dataURL={getDataURL()}
                                        isRefreshData={`${refreshReason}-${JSON.stringify(filters)}`}
                                        isEditActionEnabled={false}
                                        isViewActionEnabled={false}
                                        isDeleteActionEnabled={false}
                                        actionColumnSettings={{ width: 1 }}
                                        rowClassName={(record) => {
                                            const cid = record.contextId;
                                            if (pendingContextIds.has(cid)) return 'live-request-row-blink';
                                            if (activeContextIds.has(cid)) return 'live-request-row-active';
                                            return '';
                                        }}
                                    />
                                </div>
                            </div>

                        </div>
                    </PageLayout >
                </>
                : <PageLayout
                    pageName={'No Access'}
                    pageTitle={'No Access'}
                    bodyClassName={`module-main-wrp no-access-page`}
                    key={`${appState?.selectedLang}`}
                >
                    <div className="container">
                        <div className="main-content-wrapper AI-module-list f-wrp">
                            <div className='no-access-box'>
                                <div className='no-access-title'>
                                    <h2 style={{ color: '#fff', textAlign: 'center' }}>{translateThis('No Access')}</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </PageLayout>}
        </>
    )
}
ServiceTicketList.propTypes = {}

export default ServiceTicketList;
